import React from "react";
import { motion } from "framer-motion";
import { OTT } from "../data/content";

/* Section: Start booking profits early — AVOD / SVOD / TVOD */
export default function OTTDistribution() {
  return (
    <section id="ott" className="vw-section relative overflow-hidden" data-testid="vw-ott">
      {/* Ambient */}
      <div className="pointer-events-none absolute -top-32 right-0 w-[600px] h-[600px] rounded-full opacity-20"
           style={{ background: "radial-gradient(circle, var(--vw-orange), transparent 60%)" }} />
      <div className="pointer-events-none absolute inset-0 vw-grid opacity-25" />

      <div className="vw-container relative">
        <div className="mb-16 grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-end">
          <div>
            <div className="vw-eyebrow mb-6">{OTT.eyebrow}</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-[14ch]">
              {OTT.headline}
            </h2>
            <p className="mt-6 font-serif-italic text-2xl md:text-3xl text-[var(--vw-text-dim)] max-w-[36ch] leading-[1.25]">
              — {OTT.serifAccent}<span className="text-[var(--vw-orange)]">.</span>
            </p>
          </div>
          <p className="text-[var(--vw-text-dim)] leading-relaxed text-lg max-w-md">
            {OTT.body}
          </p>
        </div>

        {/* Three channels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--vw-border)] border border-[var(--vw-border)]">
          {OTT.channels.map((c, i) => (
            <motion.div
              key={c.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="relative group bg-[var(--vw-bg)] p-8 md:p-10 hover:bg-[var(--vw-surface)] transition-colors min-h-[380px] flex flex-col"
              data-testid={`vw-ott-channel-${c.code.toLowerCase()}`}
            >
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-display text-[5rem] leading-none tracking-tighter vw-text-gradient-warm">
                  {c.code}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
                  CH · {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-display text-2xl leading-tight text-[var(--vw-text)] mb-4">
                {c.name}
              </h3>
              <p className="text-[var(--vw-text-dim)] leading-relaxed mb-6 flex-1">
                {c.desc}
              </p>
              <div className="border-t border-[var(--vw-border)] pt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-1">
                  PARTNER RAILS
                </div>
                <div className="font-mono text-sm text-[var(--vw-text)]">
                  {c.partners}
                </div>
              </div>
              <span className="absolute top-0 left-0 h-px w-0 bg-[var(--vw-orange)] group-hover:w-full transition-all duration-700" />
            </motion.div>
          ))}
        </div>

        {/* Benefits + CTA */}
        <div className="mt-14 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
            {OTT.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[var(--vw-text-dim)] leading-relaxed">
                <span className="mt-2 w-4 h-px bg-[var(--vw-orange)] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a href="#contact" className="vw-btn-primary shrink-0 justify-self-start lg:justify-self-end" data-testid="vw-ott-cta">
            Explore Distribution Terms
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
}
